/**
 * «Собери фразу» · окно 3 доводки (26.07) — сборка предложения КНОПКАМИ-СЛОВАМИ.
 *
 * Зачем отдельный файл: это вторая часть витрины с настоящей ЛОГИКОЙ (первая —
 * `grammar.ts`), и её надо уметь проверить запуском, как модули ядра:
 *
 *   cd Lang/web && node tools/check-build.mjs
 *
 * Довод владельца 26.07: «вписывать руками уместно на проверке правописания, а
 * угадывание — выбором из списка слов». Согласились: клавиатура = продукция
 * (достать из памяти + написание), сбор из слов = узнавание + **порядок слов**,
 * которого у нас не проверяло НИЧТО. Заодно этот вид лечит разрывной оборот из
 * 2–3 пропусков (69 карточек), который на телефоне разъезжался по строкам.
 *
 * Закон вида:
 *   • предложение берём из ГОТОВОЙ заготовки `examples.text_gapped` + `target_form`
 *     (те же данные, что кормят «Контекст») — новых полей в базе не заводим;
 *   • слово = одна кнопка. Составная форма («call round») — ДВЕ кнопки: иначе
 *     кнопка выдавала бы весь фразовый глагол целиком;
 *   • ⭐ среди кнопок ЛИШНИЕ: сперва другие формы того же слова (`lexicon.forms`
 *     и лемма — `go` рядом с `gone` это и есть ловушка на форму), потом слова
 *     соседних карточек подхода;
 *   • порядок проверяется целиком: «слова те, но не в том порядке» — отдельный
 *     разбор промаха, а не общий «мимо»;
 *   • оценка как в «Выборе» и «Контексте»: собрал сразу → 2, со второй попытки → 1,
 *     не собрал → 0.
 *
 * ⚠ Заглавная буква первого слова и точка последнего оставлены как в предложении:
 * это естественный вид (так же делают все сборщики фраз) и подсказывают они только
 * КРАЯ, а проверяем мы форму и порядок в середине. Захочет владелец строже — это
 * одна строка в `tokens`.
 *
 * ⛔ Здесь нет ни сети, ни DOM, ни SRS: только строки. Оценку в память кладёт ядро.
 */

import type { Card } from "./api";
import { formList, gapSpec, isBlankForm, norm, variants } from "./grammar";

/** Короче трёх слов собирать нечего, длиннее двенадцати — мучение на телефоне. */
export const MIN_TOKENS = 3;
export const MAX_TOKENS = 12;
/** Сколько лишних слов кладём в набор. */
export const WANT_EXTRAS = 3;

export interface BuildSpec {
  /** правильная последовательность слов */
  tokens: string[];
  /** места, где стоит изучаемое слово (их подсвечиваем в разборе) */
  targets: number[];
  /** лишние слова: формы того же слова + слова соседних карточек */
  extras: string[];
  /** что показываем кнопками (правильные + лишние, перемешаны стабильно) */
  bank: string[];
  /** перевод предложения — это и есть задание; без него собирать нечего */
  ru: string;
}

export type BuildMiss = "form" | "order" | "extra" | "short";

export type BuildVerdict = { ok: true } | { ok: false; kind: BuildMiss; at: number };

/* ── разбор предложения ───────────────────────────────────────────────────── */

function words(text: string): string[] {
  return String(text || "").trim().split(/\s+/).filter(Boolean);
}

/**
 * Перемешивание с постоянным зерном: перерисовка экрана (пришли свежие цифры,
 * сменилась тема) НЕ должна менять порядок кнопок под пальцем.
 * ⚠ Свой, а не общий с «Выбором»: тот живёт в принятом коде занятия, и трогать
 * его ради переиспользования восьми строк — риск ради красоты.
 */
export function shuffleSeeded<T>(list: T[], seed: number): T[] {
  const out = list.slice();
  let s = Math.abs(Math.round(seed)) * 9301 + 49297;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Все написания слова, из которых можно сделать ЛИШНЮЮ кнопку (лемма + формы). */
function formCandidates(c: Card): string[] {
  const out = [String(c.word || "")];
  for (const v of Object.values(c.forms || {})) {
    if (!isBlankForm(v)) out.push(String(v));
  }
  for (const f of formList(c)) out.push(f);   // формы из примера (обычно уже в предложении)
  return out;
}

/**
 * Собирается ли из готовых данных «Собери фразу»? Нет — вернём null, и занятие
 * честно покажет карточку «Выбором» (пустых экранов человек видеть не должен).
 *
 * `pool` — слова соседних карточек подхода: ими добираем лишние, когда у слова
 * своих форм нет (`water` — существительное без форм в базе).
 */
export function buildSpec(c: Card, pool: string[] = [], want = WANT_EXTRAS): BuildSpec | null {
  const spec = gapSpec(c);
  if (!spec) return null;
  const ru = (c.example?.ru || "").trim();
  if (!ru) return null;             // без перевода задание непонятно: что именно собирать?

  const tokens: string[] = [];
  const targets: number[] = [];
  /** Приклеить к последнему слову то, что в предложении стоит БЕЗ пробела перед ним. */
  const glue = (text: string): string => {
    if (!text || /^\s/.test(text) || !tokens.length) return text;
    const stuck = (text.match(/^\S+/) as RegExpMatchArray)[0];
    tokens[tokens.length - 1] += stuck;      // «_____,» → кнопка «water,», а не «,» отдельно
    return text.slice(stuck.length);
  };
  for (const w of words(spec.chunks[0])) tokens.push(w);
  for (let i = 0; i < spec.targets.length; i++) {
    // Составная форма («call round») = две кнопки: одной кнопкой мы выдали бы ответ.
    for (const w of words(spec.targets[i])) { targets.push(tokens.length); tokens.push(w); }
    // Знак препинания сразу после пропуска — часть слова, а не отдельная кнопка
    // (поймано живой обкаткой: у `water` в наборе оказалась одинокая запятая).
    for (const w of words(glue(spec.chunks[i + 1] ?? ""))) tokens.push(w);
  }
  if (!targets.length || tokens.length < MIN_TOKENS || tokens.length > MAX_TOKENS) return null;

  const used = new Set(tokens.map(norm));
  const extras: string[] = [];
  const addExtra = (raw: string): void => {
    const t = String(raw || "").trim();
    const n = norm(t);
    if (!n || used.has(n) || extras.length >= want) return;
    used.add(n);                    // повтор того же слова кнопкой не показываем
    extras.push(t);
  };
  // ⭐ Сперва формы ТОГО ЖЕ слова: `go` рядом с `gone` — ровно та ловушка, ради
  // которой делался «Контекст», только без клавиатуры. Хвост составного слова
  // («called round» → `round`) уже стоит в предложении и лишним не станет.
  for (const cand of formCandidates(c)) for (const w of words(cand)) addExtra(w);
  for (const p of pool) { const first = words(p)[0]; if (first) addExtra(first); }

  return {
    tokens,
    targets,
    extras,
    bank: shuffleSeeded([...tokens, ...extras], c.word_id || 1),
    ru,
  };
}

/* ── сверка собранного ────────────────────────────────────────────────────── */

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const x = a.slice().sort();
  const y = b.slice().sort();
  return x.every((v, i) => v === y[i]);
}

/**
 * Сверка. Разбор промаха важнее вердикта: человеку надо сказать, ЧТО не так —
 * форма, порядок или лишнее слово. Порядок разбора:
 *   1) не все слова выложены → `short` (⚠ попытку это НЕ жжёт: сколько слов в
 *      предложении, человек заранее не знает — среди кнопок есть лишние);
 *   2) набор слов ТОТ ЖЕ, а порядок другой → `order`. Идёт раньше формы нарочно:
 *      переставленные местами части оборота («was up blown») — это порядок, хотя
 *      обе части и есть формы того же слова;
 *   3) разошлось только там, где стоит изучаемое слово, и выложена его же
 *      другая форма → `form` (то самое, ради чего вид и делался);
 *   4) иначе → `extra` (лишнее или не то слово).
 */
export function checkBuild(picked: string[], spec: BuildSpec, c: Card): BuildVerdict {
  const a = picked.map(norm).filter(Boolean);
  const b = spec.tokens.map(norm);
  if (a.length === b.length && a.every((t, i) => t === b[i])) return { ok: true };
  if (a.length < b.length) return { ok: false, kind: "short", at: a.length };

  const diff: number[] = [];
  for (let i = 0; i < Math.max(a.length, b.length); i++) if (a[i] !== b[i]) diff.push(i);
  const at = diff.length ? diff[0] : 0;

  if (a.length === b.length) {
    if (sameSet(a, b)) return { ok: false, kind: "order", at };
    const known = variants(c);
    const onlyForm = diff.length > 0 &&
      diff.every((i) => spec.targets.includes(i) && known.has(a[i]));
    if (onlyForm) return { ok: false, kind: "form", at };
  }
  return { ok: false, kind: "extra", at };
}

/**
 * Оценка по числу промахов — тот же закон, что у «Выбора» и «Контекста»
 * (решение 22.07): собрал сразу → 2, со второй попытки → 1, не собрал → 0.
 */
export function gradeFor(misses: number): 0 | 1 | 2 {
  if (misses <= 0) return 2;
  if (misses === 1) return 1;
  return 0;
}
